import { Entity, SystemInterface } from '@goodboydigital/odie';
import { Texture, utils } from 'pixi.js';
import { Signal } from 'typed-signals';

import { Settings } from '../../AppConfig';
import { ColorMeta } from '../../plugins/DataPlugin';
import { Game } from '../Game';
import { GarageSystem } from './GarageSystem';

export type ShopType = 'default' | 'body' | 'wheel' | 'trail' | 'extras' | 'paint';

export interface ShopIconMeta
{
    itemId: string,
    texture: Texture,
    tint?: number,
    name: string,
    unlocked: boolean,
    coinCost?: number,
    diamondCost?: number,
}

export interface PaintMeta
{
    id: string,
    index: number,
    tint: number,
    active: boolean,
    selected: boolean,
    coinCost: number,
    diamondCost: number,
    unlocked: boolean
}

/**
 * Manages the purchased/unlocked states and prices for items
 */
export class ShopSystem implements SystemInterface
{
    public game: Game;
    public characters: string[];
    public wheels: string[];
    public chassis: string[];

    protected purchasedContent: Record<'bike'|'character'|'wheel'|'trail'|'deco'|'color', Record<string, string>>;
    protected garage: GarageSystem;

    public signals: {
        colorUnlocked: Signal<(colorId: string) => void>
    };

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;

        this.garage = this.game.garage;

        this.characters = [];
        this.chassis = [];
        this.wheels = [];
        this.purchasedContent = {
            bike: {},
            character: {},
            wheel: {},
            trail: {},
            deco: {},
            color: {},
        } as Record<'bike'|'character'|'wheel'|'trail'|'deco'|'color', Record<string, string>>;

        this.signals = {
            colorUnlocked: new Signal<(colorId: string) => void>(),
        };

        this.load();
    }

    /** Load selection from local storage */
    public load(): void
    {
        this.purchasedContent = this.game.garage.user.data.purchasedContent;
        
        if(Settings.unlockAllShopContent){
            this.debugUnlockAllContent();
        }
        
        const vehicleData = this.game.garage.user.data.vehicle;

        // Ensure the currently selected parts are purchased
        this.purchaseItem(vehicleData.character, 'character');
        this.purchaseItem(vehicleData.wheelLeft, 'wheel');
        this.purchaseItem(vehicleData.chassis, 'bike');
        this.purchaseItem(vehicleData.colorChassis, 'color');
        this.purchaseItem(vehicleData.colorWheelLeft, 'color');
        this.purchaseItem(vehicleData.colorWheelRight, 'color');
        this.purchaseItem(vehicleData.trailLeft, 'trail');
        this.purchaseItem(vehicleData.extra, 'deco');

    }

    // ===== CHECK/PURCHASE CONTENT ==========================================================================================
    public checkPurchased(id: string, type: 'wheel' | 'trail' | 'bike' | 'character' | 'deco' | 'color'): boolean
    {
        const data = this.game.app.data.getData<any>(type, id);

        if (data && data.unlocked) return true;

        return !!this.purchasedContent[type][id];
    }

    public checkCoinsAvailable(coin: number): boolean
    {
        return coin <= this.game.garage.user.data.coins || Settings.shopDebug;
    }

    public checkDiamondsAvailable(diamonds: number): boolean
    {
        return diamonds <= this.game.garage.user.data.diamonds || Settings.shopDebug;
    }

    public purchaseItem(
        id: string,
        type: 'wheel' | 'trail' | 'bike' | 'character' | 'deco' | 'color',
        coins = 0,
        diamonds = 0,
    ): void
    {
        this.purchasedContent[type][id] = id;
        const user = this.game.garage.user.data;

        if (Settings.shopDebug)
        {
            coins = 0;
            diamonds = 0;
        }

        user.purchasedContent[type][id] = id;
        user.coins -= coins;
        user.diamonds -= diamonds;

        this.game.garage.user.save();
        this.game.hud.coinsWidget.refresh();
        this.game.hud.diamondWidget.refresh();
        
        if(coins > 0 || diamonds > 0){
            
        }
    }

    // ===== SET PARTS BY ID ==========================================================================================

    public setWheelLeft(id: string): void { this.garage.setWheelLeft(id); }
    public setWheelRight(id: string): void { this.garage.setWheelRight(id); }
    public setChassis(id: string): void { this.garage.setChassis(id); }
    public setTrail(id: string): void { this.garage.setTrail(id); }
    public setExtra(id: string): void { this.garage.setExtra(id); }
    public setCharacter(id: string): void { this.garage.setCharacter(id); }
    public setColor(color: string): void { this.garage.setColor(color); }
    public setColorChassis(color: string): void { this.garage.setColorChassis(color); }
    public setColorWheelLeft(color: string): void { this.garage.setColorWheelLeft(color); }
    public setColorWheelRight(color: string): void { this.garage.setColorWheelRight(color); }
    public saveVehicleSelection(): void { this.garage.saveVehicleSelection(); }

    // ===== UTILITY ==================================================================================================

    public update(): void
    {
        // Only to make a valid SystemInterface
    }

    public getPaintMeta(): PaintMeta[]
    {
        const colorMeta: PaintMeta[] = [];

        this.game.app.assets.getData<Record<string, any>>('colors').slice(0).forEach((data: ColorMeta, index: number) =>
        {
            // item is free from store
            if (data.unlocked) this.purchaseItem(data.val, 'color', 0, 0);

            colorMeta.push({
                id: data.val,
                index,
                tint: utils.string2hex(data.val),
                active: this.garage.selectedColor === data.val,
                selected: this.garage.selectedColor === data.val,
                coinCost: data.coinCost,
                diamondCost: data.diamondCost,
                unlocked: this.checkPurchased(data.val, 'color'),
            });
        });

        return colorMeta;
    }
    
    public areAllPaintsUnlocked(): boolean
    {
        var anyUnpurchased = false;
        this.game.app.assets.getData<Record<string, any>>('colors').slice(0).forEach((data: ColorMeta, index: number) =>
        {
            if(!this.checkPurchased(data.val, 'color')){
                anyUnpurchased = true;
            }
        });
        return !anyUnpurchased;
    }
    
    public checkAllTypeUnlocked(type: 'wheel' | 'trail' | 'bike' | 'character' | 'deco'): boolean
    {
        var anyUnpurchased = false;
        
        var content: string[] = [];
        
        switch(type){
            case 'wheel':{
                content = this.garage.wheels;
                break;
            }
            case 'bike':{
                content = this.garage.chassis;
                break;
            }
            case 'trail':{
                content = this.garage.trails;
                break;
            }
            case 'deco':{
                content = this.garage.extras;
                break;
            }
            case 'character':{
                content = this.garage.characters;
                break;
            }
        }

        for(var i = 0; i<content.length; i++){
            if(!this.checkPurchased(content[i], type)){
                anyUnpurchased = true;
            }
        }
        return !anyUnpurchased;
    }
    
    private debugUnlockAllContent(): void
    {
        var availableParts = this.game.app.data.getAvailableVehicleParts();
        var availableColors = this.game.app.data.getAvailableColors();
        
        for(var i = 0; i<availableParts.character.length; i++){
            this.purchasedContent.character[availableParts.character[i]] = availableParts.character[i];
        }
        for(var i = 0; i<availableParts.deco.length; i++){
            this.purchasedContent.deco[availableParts.deco[i]] = availableParts.deco[i];
        }
        for(var i = 0; i<availableParts.wheel.length; i++){
            this.purchasedContent.wheel[availableParts.wheel[i]] = availableParts.wheel[i];
        }
        for(var i = 0; i<availableParts.trail.length; i++){
            this.purchasedContent.trail[availableParts.trail[i]] = availableParts.trail[i];
        }
        for(var i = 0; i<availableParts.chassis.length; i++){
            this.purchasedContent.bike[availableParts.chassis[i]] = availableParts.chassis[i];
        }
        for(var i = 0; i<availableColors.length; i++){
            this.purchasedContent.color[availableColors[i]] = availableColors[i];
        }
    }
}
